import React, { useLayoutEffect, useState } from "react";
import {
  InboxOutlined,
  RedoOutlined,
  RollbackOutlined,
  FolderOpenOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  Layout,
  Tabs,
  Upload,
  Input,
  List,
  Spin,
  Card,
  Col,
  Row,
  Divider,
  Button,
  Form,
  Space,
  Modal,
} from "antd";
import { displayMessage, postAPI, getAPI } from "../../utils/common";
import {
  IMAGE_BASE_URL,
  FILE_UPLOAD,
  FILES_BY_FOLDER,
  FOLDERS,
  FILE_UPDATE,
} from "../../constants/api";
import { ERROR_MSG_TYPE, SUCCESS_MESSAGE } from "../../constants/messages";
// import folder from "../../assets/image/folder.png";
import folder from "../../assets/image/folder-2.webp";
import LayoutHeader from "../inc/LayoutHeader";

const { Search } = Input;
const { Dragger } = Upload;
const { Meta } = Card;

const FileUploadForm = ({
  form,
  folderList,
  fileList,
  setFileList,
  loading,
  handleUpload,
}) => (
  <Form
    form={form}
    name="validate_other"
    onFinish={handleUpload}
    layout="vertical"
  >
    <Row>
      <Col span={11} offset={1} center>
        <Dragger
          multiple
          onRemove={(file) => setFileList(fileList.filter((f) => f !== file))}
          beforeUpload={(file) => {
            const allowedTypes = [
              "image/webp",
              "image/png",
              "image/jpeg",
              "image/jpg",
              "image/gif",
            ];
            const isAllowed = allowedTypes.includes(file.type);
            if (isAllowed) {
              setFileList([...fileList, file]);
            } else {
              displayMessage(
                ERROR_MSG_TYPE,
                `${file.name} is not a valid image file (WEBP, PNG, JPEG, JPG, GIF only)`
              );
            }
            return isAllowed || Upload.LIST_IGNORE;
          }}
          fileList={fileList}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Only (WEBP, PNG, JPEG, JPG) format supported
          </p>
        </Dragger>
      </Col>
      <Col span={11} offset={1} center>
        <center>
          <Form.Item
            name="tag"
            label="Tag"
            rules={[{ required: true, message: "Please input tag!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="folder"
            label="Folder"
            rules={[{ required: true, message: "Please input folder!" }]}
          >
            <Input />
          </Form.Item>
          {folderList.length > 0 && (
            <div>
              <Divider orientation="left">Folders</Divider>
              <Space wrap="wrap" gap="small">
                {folderList.map((item) => (
                  <Button
                    key={item.folder}
                    style={{ maxWidth: "150px", margin: "2px" }}
                    onClick={() => form.setFieldValue("folder", item.folder)}
                    type="dashed"
                    icon={<FolderOpenOutlined />}
                  >
                    {item.folder}
                  </Button>
                ))}
              </Space>
            </div>
          )}
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                disabled={fileList.length === 0}
                loading={loading}
                style={{ marginTop: 16 }}
              >
                {loading ? "Uploading" : "Start Upload"}
              </Button>
              <Button htmlType="reset" style={{ marginTop: 16 }}>
                Reset
              </Button>
            </Space>
          </Form.Item>
        </center>
      </Col>
    </Row>
  </Form>
);

const FileList = ({
  folderList,
  uploadedFileList,
  searchQuery,
  getFileList,
}) => (
  <List
    grid={{ gutter: 16, column: 7 }}
    dataSource={folderList.filter(
      (item) =>
        typeof item.folder === "string" &&
        item.folder.toLowerCase().includes(searchQuery.toLowerCase())
    )}
    renderItem={(item) => (
      <Card
        style={{
          backgroundColor: "white",
          padding: "5px",
          margin: "2px",
          textAlign: "center",
          cursor: "pointer",
        }}
        key={item.folder}
        onClick={() => getFileList(item.folder)}
        cover={
          <img
            alt="folder"
            src={folder}
            style={{ width: "100%", padding: "20px" }}
          />
        }
      >
        <Meta title={item.folder} />
      </Card>
    )}
  />
);

const UploadedFileList = ({
  uploadedFileList,
  searchQuery,
  IMAGE_BASE_URL,
  getFileList,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [newImage, setNewImage] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  const handleImageClick = (imagePath) => {
    setSelectedImage(imagePath);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedImage(null);
  };

  const handleEditClick = (item) => {
    setIsEditMode(true);
    setEditingItem(item);
  };

  const handleFileChange = (e) => {
    const file = e.fileList[0]?.originFileObj; // Get the selected file
    if (file) {
      setNewImage(file);
    }
  };

  const handleSaveNewImage = () => {
    if (newImage && editingItem) {
      setIsUploading(true);
      const data = new FormData();
      data.append("folder", editingItem.folder);
      data.append("id", editingItem.id);
      data.append("files[]", newImage);
      data.append("tag", editingItem.tag);

      const successFn = (data) => {
        getFileList(editingItem.folder);
        setIsUploading(false);
        setIsEditMode(false);
        setNewImage(null);
        setEditingItem(null);
        displayMessage(SUCCESS_MESSAGE, data.message);
      };
      const errorFn = () => {
        getFileList(editingItem.folder);
        setIsUploading(false);
        setIsEditMode(false);
        setNewImage(null);
        setEditingItem(null);
        displayMessage(ERROR_MSG_TYPE, "Unable to Save Data");
      };

      postAPI(FILE_UPDATE, data, successFn, errorFn);
    }
  };

  return (
    <>
      <List
        grid={{ gutter: 16, column: 7 }}
        dataSource={uploadedFileList.filter((item) =>
          item.tag.toLowerCase().includes(searchQuery.toLowerCase())
        )}
        renderItem={(item) => (
          <Card
            style={{
              backgroundColor: "white",
              padding: "5px",
              margin: "2px",
              textAlign: "center",
              cursor: "pointer",
            }}
            actions={[
              <EditOutlined key="edit" onClick={() => handleEditClick(item)} />,
            ]}
            key={item.id}
            cover={
              <img
                style={{ width: "100%", height: "200px", objectFit: "contain" }}
                alt="files"
                src={IMAGE_BASE_URL + item.filePath}
                onClick={() => handleImageClick(item.filePath)}
              />
            }
          >
            <Meta
              title={item.tag}
              description={`${item.folder}/${item.filePath}`}
            />
          </Card>
        )}
      />

      <Modal
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width="80%"
      >
        <img
          alt="view-mode"
          style={{ width: "100%", objectFit: "contain" }}
          src={IMAGE_BASE_URL + selectedImage}
        />
      </Modal>

      {/* Edit Modal for updating the image */}
      <Modal
        title="Edit Image"
        visible={isEditMode}
        loading={isUploading}
        onCancel={() => setIsEditMode(false)}
        onOk={handleSaveNewImage}
      >
        <Upload
          accept="image/*"
          showUploadList={false}
          beforeUpload={() => false} // Prevent auto upload
          onChange={handleFileChange}
        >
          <Button>Choose a new image</Button>
        </Upload>
        {newImage && (
          <div style={{ marginTop: "10px" }}>
            <img
              src={URL.createObjectURL(newImage)}
              alt="new-preview"
              style={{ width: "100%", objectFit: "contain" }}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

const FilesManager = () => {
  const { Content } = Layout;
  const [uploadedFileList, setUploadedFilesList] = useState([]);
  const [folderList, setFolderList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [form] = Form.useForm();

  const handleUpload = async (values) => {
    setLoading(true);
    const data = new FormData();
    data.append("folder", values.folder);
    fileList.forEach((file) => data.append("files[]", file));
    data.append("tag", values.tag);

    const successFn = (data) => {
      setLoading(false);
      form.resetFields();
      setFileList([]);
      getFileList(values.folder);
      displayMessage(SUCCESS_MESSAGE, data.message);
    };

    const errorFn = () => {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to Save Data");
    };

    postAPI(FILE_UPLOAD, data, successFn, errorFn);
  };

  const getFolderList = () => {
    setLoading(true);
    const successFn = (data) => {
      setLoading(false);
      if (data.status) setFolderList(data.data);
      else displayMessage(ERROR_MSG_TYPE, data.message);
    };
    const errorFn = () => {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to Get Data");
    };
    getAPI(FOLDERS, successFn, errorFn);
  };

  const getFileList = (folderName) => {
    setLoading(true);
    const data = new FormData();
    data.append("folder", folderName);
    const successFn = (data) => {
      setLoading(false);
      if (data.status) setUploadedFilesList(data.data);
      else displayMessage(ERROR_MSG_TYPE, data.message);
    };
    const errorFn = () => {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to Get Data");
    };
    postAPI(FILES_BY_FOLDER, data, successFn, errorFn);
  };

  useLayoutEffect(() => {
    getFolderList();
  }, []);

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <div style={{ backgroundColor: "white", borderRadius: "10px" }}>
          <Tabs centered defaultActiveKey="1">
            <Tabs.TabPane tab="FILES" key="1">
              <Spin spinning={loading}>
                <Card
                  bordered={false}
                  extra={
                    <Space>
                      <Search
                        style={{ paddingTop: 10, paddingBottom: 10 }}
                        placeholder="Search by Folder or Tag"
                        onSearch={setSearchQuery}
                        enterButton
                      />
                      <Button
                        type="default"
                        onClick={() =>
                          uploadedFileList.length > 0
                            ? setUploadedFilesList([])
                            : getFolderList()
                        }
                      >
                        {uploadedFileList.length > 0 ? (
                          <RollbackOutlined />
                        ) : (
                          <RedoOutlined />
                        )}
                      </Button>
                    </Space>
                  }
                >
                  <FileList
                    folderList={folderList}
                    uploadedFileList={uploadedFileList}
                    searchQuery={searchQuery}
                    getFileList={getFileList}
                  />
                  {uploadedFileList.length > 0 && (
                    <>
                      <Divider />
                      <UploadedFileList
                        uploadedFileList={uploadedFileList}
                        searchQuery={searchQuery}
                        IMAGE_BASE_URL={IMAGE_BASE_URL}
                        getFileList={getFileList}
                      />
                    </>
                  )}
                </Card>
              </Spin>
            </Tabs.TabPane>
            <Tabs.TabPane tab="UPLOAD NEW" key="2">
              <Spin spinning={loading}>
                <Card bordered={false}>
                  <FileUploadForm
                    form={form}
                    folderList={folderList}
                    fileList={fileList}
                    setFileList={setFileList}
                    loading={loading}
                    handleUpload={handleUpload}
                  />
                </Card>
              </Spin>
            </Tabs.TabPane>
          </Tabs>
        </div>
      </Content>
    </Layout>
  );
};

export default FilesManager;
